import { Fragment, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Button, Loader } from '../../components'
import { createCycleCountAssignment } from '../../services/cycleCountServices'
import { XIcon } from '@heroicons/react/solid'
import { useTranslation } from 'react-i18next'
import { OperatorSelectTable } from './OperatorSelectTable'
import { localizeReviewError } from './reviewErrors'

const T_PREFIX = 'cycle_counts.assign_modal'

export const AssignCycleCountDialog = ({ open, warehouseId, selectedLocations = [], onSuccess = () => {}, onCancel = () => {} }) => {
  const { i18n } = useTranslation()
  const [selectedOperator, setSelectedOperator] = useState(null)
  const [busy, setBusy] = useState(false)

  const onSubmit = async () => {
    if (!selectedOperator || selectedLocations.length === 0) return
    setBusy(true)
    try {
      const result = await createCycleCountAssignment({
        assigned_to_id: selectedOperator.id,
        warehouse_id: parseInt(warehouseId),
        location_ids: [...new Set(selectedLocations.map(loc => loc._original?.id || loc.id))],
      })
      setBusy(false)
      setSelectedOperator(null)
      onSuccess(result)
    } catch (error) {
      setBusy(false)
      const err = error.response?.data?.error
      const errorCode = error.response?.data?.error_code
      const rawMsg = typeof err === 'string' ? err : err?.message || i18n.t(`${T_PREFIX}.error`)
      window.alert(localizeReviewError(rawMsg, errorCode))
    }
  }

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="fixed z-10 inset-0 overflow-y-auto" onClose={() => {}}>
        <div className="flex items-center justify-center min-h-screen">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="relative bg-white rounded-lg shadow-xl sm:max-w-5xl sm:w-full p-6 text-gray-600 my-3 overflow-hidden max-h-[length:90vh]">
              <Loader show={busy} />
              <div className="absolute top-0 right-0 p-5 cursor-pointer" onClick={onCancel}>
                <XIcon className="h-5 w-5" />
              </div>
              <Dialog.Title className="text-lg text-center">{i18n.t(`${T_PREFIX}.title`)}</Dialog.Title>
              <p className="text-sm text-center text-gray-500 mb-4">{i18n.t(`${T_PREFIX}.selected_count`, { count: selectedLocations.length })}</p>

              <OperatorSelectTable selectedOperator={selectedOperator} onOperatorSelect={setSelectedOperator} warehouseId={warehouseId} role="ROLE_OPERATOR" />

              <div className="mt-3 flex justify-between">
                <Button className="m-1" type="secondary" onClick={onCancel}>
                  {i18n.t(`${T_PREFIX}.cancel`)}
                </Button>
                <Button className="m-1" type="primary" disabled={!selectedOperator} onClick={onSubmit} loading={busy}>
                  {selectedOperator
                    ? `${i18n.t(`${T_PREFIX}.assign`)} ${selectedLocations.length} ${i18n.t(`${T_PREFIX}.locations`)}`
                    : i18n.t(`${T_PREFIX}.choose_operator`)}
                </Button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
